import { getFirestore, doc, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove, serverTimestamp, Firestore } from 'firebase/firestore';
import { FirebaseAppService } from './firebase-app';
import { User, UserRef } from '../interfaces/user';
import { OrganizationRef } from '../interfaces/organization';
import { Log } from './log';

class UsersController {

  private get db(): Firestore {
    return getFirestore(FirebaseAppService.app);
  }

  toRef(user: User): UserRef {
    return { id: user.id, name: user.name, email: user.email };
  }

  async getProfile(userId: string): Promise<User | null> {
    try {
      const snap = await getDoc(doc(this.db, 'users', userId));
      return snap.exists() ? (snap.data() as User) : null;
    } catch (error) {
      Log.error('Failed to get user profile', error);
      throw error;
    }
  }

  async createProfile(user: User): Promise<void> {
    try {
      const emailDomain = user.email?.split('@')[1] ?? '';
      await setDoc(doc(this.db, 'users', user.id), {
        id: user.id,
        email: user.email,
        name: user.name,
        emailVerified: user.emailVerified,
        emailDomain,
        organizations: user.organizations ?? [],
        defaultOrganizationId: user.defaultOrganizationId ?? null,
        createdAt: serverTimestamp(),
      });
      Log.debug('User profile created', user.id);
    } catch (error) {
      Log.error('Failed to create user profile', error);
      throw error;
    }
  }

  async updateProfile(userId: string, data: Partial<User>): Promise<void> {
    try {
      await updateDoc(doc(this.db, 'users', userId), data as any);
      Log.debug('User profile updated', userId);
    } catch (error) {
      Log.error('Failed to update user profile', error);
      throw error;
    }
  }

  async addOrganization(userId: string, orgRef: OrganizationRef): Promise<void> {
    try {
      await updateDoc(doc(this.db, 'users', userId), {
        organizations: arrayUnion(orgRef),
      });
      Log.debug(`Organization ${orgRef.id} added to user ${userId}`);
    } catch (error) {
      Log.error('Failed to add organization to user', error);
      throw error;
    }
  }

  async removeOrganization(userId: string, orgRef: OrganizationRef): Promise<void> {
    try {
      await updateDoc(doc(this.db, 'users', userId), {
        organizations: arrayRemove(orgRef),
      });
      Log.debug(`Organization ${orgRef.id} removed from user ${userId}`);
    } catch (error) {
      Log.error('Failed to remove organization from user', error);
      throw error;
    }
  }

  async setDefaultOrganization(userId: string, orgId: string): Promise<void> {
    try {
      await updateDoc(doc(this.db, 'users', userId), { defaultOrganizationId: orgId });
      Log.debug(`Default organization set to ${orgId} for user ${userId}`);
    } catch (error) {
      Log.error('Failed to set default organization', error);
      throw error;
    }
  }
}

export const UsersService = new UsersController();
